import { format } from 'date-fns';
import { AlertCircle, CheckCircle2, Video, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  KGSummaryStatus,
  KGResolutionType,
  KGSummaryResponse,
} from '@/lib/api/knowledgeGaps';
import { CATEGORY_ICON_MAP } from './constants';

export function getCategoryMeta(category: string): { icon: LucideIcon; label: string } {
  if (category in CATEGORY_ICON_MAP) {
    return CATEGORY_ICON_MAP[category];
  }
  return {
    icon: AlertCircle,
    label: category
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  };
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

// Sidebar status badge for non-active, non-in-flight rows. The existing
// "Optimizing" badge on the sidebar card already covers resolving_via_prompt
// / in_progress_prompt_update — returning null for those avoids duplicating
// it. resolving_via_product_capture renders a dedicated "Capturing" badge.
export function getStatusBadge(
  status: KGSummaryStatus,
): { label: string; icon: LucideIcon; className: string } | null {
  if (status === 'resolved') {
    return {
      label: 'Resolved',
      icon: CheckCircle2,
      className:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200',
    };
  }
  if (status === 'dismissed') {
    return {
      label: 'Dismissed',
      icon: XCircle,
      className: 'bg-muted text-muted-foreground border-border',
    };
  }
  if (status === 'resolving_via_product_capture') {
    return {
      label: 'Capturing',
      icon: Video,
      className:
        'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200',
    };
  }
  return null;
}

export function getFrequencyMeta(count: number): {
  label: string;
  detail: string;
  className: string;
} {
  const safeCount = Math.max(0, count);
  const reportLabel = `Seen ${safeCount} time${safeCount === 1 ? '' : 's'}`;

  if (safeCount >= 10) {
    return {
      label: 'High frequency',
      detail: reportLabel,
      className:
        'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300',
    };
  }
  if (safeCount >= 4) {
    return {
      label: 'Recurring',
      detail: reportLabel,
      className:
        'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300',
    };
  }
  if (safeCount >= 2) {
    return {
      label: 'Repeated',
      detail: reportLabel,
      className: 'border-border bg-muted/50 text-muted-foreground',
    };
  }
  return {
    label: 'Single report',
    detail: reportLabel,
    className: 'border-border bg-muted/30 text-muted-foreground',
  };
}

export function getResolutionMeta(
  type: KGResolutionType | null,
): { label: string; tone: 'prompt' | 'knowledge' | 'config' | 'capture' | 'unknown' } {
  if (type === 'prompt_update') return { label: 'Prompt update', tone: 'prompt' };
  if (type === 'other') return { label: 'Knowledge source', tone: 'knowledge' };
  if (type === 'config_change') return { label: 'Config change', tone: 'config' };
  if (type === 'product_capture') return { label: 'Product capture', tone: 'capture' };
  return { label: 'Unclassified', tone: 'unknown' };
}

// Concrete actions surfaced inside the Resolution Options dropdown. Used to
// decide which item carries the "Recommended" badge and to type the dropdown
// internals.
export type ResolutionAction = 'prompt_update' | 'training' | 'capture';

// Maps a gap's classifier-assigned resolution_type to the dropdown action
// that should carry the "Recommended" badge.
// - prompt_update → Resolve via prompt update
// - product_capture → Product capture
// - other → Answer in training (asset attach is hidden from the UI, so
//           training is the recommended user action for these gaps)
// - config_change / null → no recommendation surfaced
export function getRecommendedAction(
  resolutionType: KGResolutionType | null,
): ResolutionAction | null {
  if (resolutionType === 'prompt_update') return 'prompt_update';
  if (resolutionType === 'product_capture') return 'capture';
  if (resolutionType === 'other') return 'training';
  return null;
}

// Mirrors the backend eligibility for training + capture: any active gap
// whose resolution_type is not 'config_change'. config_change gaps still
// show Mark Resolved Manually + Dismiss (rendered separately in GapDetail).
export function shouldShowResolutionDropdown(
  summary: KGSummaryResponse,
): boolean {
  return (
    summary.status === 'active' &&
    summary.resolution_type !== 'config_change'
  );
}
